// Central knowledge model for MagicBolt AI - company, product families,
// products, problems, user types and industries, and the relationships
// between them. Pure data (no icons / JSX) so the same file powers:
//   - the Product Finder (/product-finder) and its matching logic
//   - the About page (/about) and feature-page "at a glance" blocks
//   - scripts/generate-knowledge.js -> public/products.json + public/llms.txt
//
// Feature-level detail (the capability list for each product) is NOT
// repeated here - each product points at its FEATURE_CATEGORIES entry in
// ./features.js via `featureId`, which stays the source of truth.
//
// Rules for editing: only record capabilities the product actually has.
// Anything unconfirmed is marked `dataRequired` instead of being guessed.

export const KNOWLEDGE_LAST_UPDATED = "2026-10-08";

export const COMPANY = {
  legalName: "MagicBolt Private Limited",
  brand: "MagicBolt AI",
  url: "https://magicbolt.ai",
  platformName: "Agency OS",
  summary:
    "MagicBolt AI is a white-label AI automation and business software platform. Agencies, freelancers and resellers use it to manage Google Business Profile, Facebook, Instagram, WhatsApp and WordPress for many local-business clients from one dashboard, alongside QR-based software (review booster, digital menu, digital business card, link-in-bio and appointment scheduling). The whole platform - sold as Agency OS - can be rebranded and resold under the agency's own name, domain and prices.",
  businessModel:
    "Agencies and resellers buy a MagicBolt AI plan, rebrand the platform with their own logo, colours and domain, and sell it to their own clients at prices they set. Clients see only the agency's brand. Plans are either channel-based (priced by the number of connected channels) or unlimited (priced per plan tier), paid annually, monthly or as a one-time lifetime payment.",
  primaryAudience: ["agency", "freelancer", "entrepreneur"],
  address: {
    streetAddress: "Zone-I, Maharana Pratap Nagar",
    addressLocality: "Bhopal",
    addressRegion: "Madhya Pradesh",
    postalCode: "462012",
    addressCountry: "IN",
  },
  email: "support@magicbolt.ai",
  phone: "+91-6263-206-697",
  officialLinks: [
    { label: "Facebook", url: "https://www.facebook.com/magicboltai/" },
    { label: "Instagram", url: "https://www.instagram.com/magicboltai/" },
    { label: "LinkedIn", url: "https://www.linkedin.com/in/magicbolt/" },
    { label: "Android app (Google Play)", url: "https://play.google.com/store/apps/details?id=com.magicbolt" },
  ],
};

export const PRODUCT_FAMILIES = [
  {
    id: "ai-automation",
    name: "AI Automation",
    description:
      "Channel automation for a business's online presence - Google Business Profile, Facebook, Instagram, WhatsApp (WhatsApp API CRM), AI Calling, WordPress - plus AI search (AEO + GEO) visibility.",
  },
  {
    id: "qr-software",
    name: "QR-Based Software",
    description:
      "Customer-facing pages opened from a QR code, NFC tap or link: review booster, digital menu, digital business card, link-in-bio / instant website and online appointment booking.",
  },
  {
    id: "agency-platform",
    name: "Agency Platform",
    description:
      "The tools an agency uses to run and resell the platform: white-label branding, client management, plans and billing, a multi-channel composer and AI content tools.",
  },
];

export const USER_TYPES = [
  {
    id: "agency",
    name: "Digital marketing agency",
    description: "Manages marketing for several local-business clients and wants one platform to deliver and resell services.",
    keywords: ["agency", "agencies", "clients", "client", "marketing agency", "digital agency"],
  },
  {
    id: "freelancer",
    name: "Freelancer / consultant",
    description: "Offers marketing or digital services to a handful of businesses and wants to productise them under their own brand.",
    keywords: ["freelancer", "freelance", "consultant"],
  },
  {
    id: "entrepreneur",
    name: "Entrepreneur / reseller",
    description: "Wants to start a software or QR-based digital business and sell it under their own brand.",
    keywords: ["reseller", "resell", "entrepreneur", "start a business", "own saas", "white label", "white-label"],
  },
  {
    id: "local-business",
    name: "Local business owner",
    description:
      "Runs a restaurant, salon, clinic, store or service business. MagicBolt AI is sold mainly to agencies and resellers who deliver it to local businesses; the AI SaaS Business plan is also described for businesses.",
    keywords: ["my business", "my shop", "my store", "my restaurant", "my salon", "owner", "local business"],
  },
];

// Industries named on the site (How Agencies Earn + feature pages). The
// product mappings are capability-based fits, not verified customer case
// studies - the UI labels them "commonly used".
export const INDUSTRIES = [
  {
    id: "restaurants",
    name: "Restaurants, cafes & bars",
    keywords: ["restaurant", "restaurants", "cafe", "bar", "food", "hotel", "hospitality", "dhaba"],
    products: ["menu", "review-routing", "gmb", "instagram", "whatsapp"],
    why: "QR menus replace printed menus, review QR standees sit on tables, and Google/Instagram drive walk-ins.",
  },
  {
    id: "salons",
    name: "Salons & spas",
    keywords: ["salon", "spa", "beauty", "parlour", "parlor", "barber"],
    products: ["appointments", "review-routing", "gmb", "instagram"],
    why: "Online booking with reminders cuts no-shows; reviews and Instagram bring new clients.",
  },
  {
    id: "clinics",
    name: "Clinics",
    keywords: ["clinic", "doctor", "dentist", "dental", "physio", "healthcare"],
    products: ["appointments", "gmb", "review-routing", "whatsapp"],
    why: "Patients book online and get reminders; a strong Google profile and reviews build trust.",
  },
  {
    id: "gyms",
    name: "Gyms & fitness centres",
    keywords: ["gym", "fitness", "yoga", "studio"],
    products: ["gmb", "review-routing", "instagram", "whatsapp"],
    why: "Local search and reviews win memberships; membership enquiries are answered and captured on WhatsApp.",
  },
  {
    id: "real-estate",
    name: "Real estate agents",
    keywords: ["real estate", "realtor", "property", "broker", "builder"],
    products: ["whatsapp", "qr-nfc", "gmb", "facebook"],
    why: "Every WhatsApp enquiry is captured as a lead and a digital business card shares listings and contact details in one tap.",
  },
  {
    id: "travel",
    name: "Tours & travel",
    keywords: ["travel", "tour", "tours", "tourism", "travel agency"],
    products: ["gmb", "review-routing", "instagram", "whatsapp"],
    why: "Reviews and Instagram content sell trips; trip enquiries are answered and captured on WhatsApp.",
  },
  {
    id: "education",
    name: "Education & coaching",
    keywords: ["coaching", "institute", "school", "tuition", "academy", "education", "classes"],
    products: ["whatsapp", "gmb", "facebook", "appointments"],
    why: "Admission enquiries are captured on WhatsApp and demo classes can be booked online.",
  },
  {
    id: "home-services",
    name: "Home services & pest control",
    keywords: ["handyman", "plumber", "electrician", "pest control", "cleaning", "repair", "service business"],
    products: ["appointments", "gmb", "review-routing", "whatsapp"],
    why: "Customers book visits online and get reminders; WhatsApp auto replies answer enquiries and reviews drive the next call.",
  },
];

// Products = the purchasable/usable modules. `featureId` links to the
// detail entry (capabilities + page URL) in ./features.js.
export const PRODUCTS_KB = [
  {
    id: "gmb",
    featureId: "gmb",
    name: "Google Business Profile Automation",
    family: "ai-automation",
    status: "ACTIVE",
    definition:
      "A tool to audit, optimise and manage Google Business Profiles: AI review replies, post and photo scheduling, geo-grid rank tracking, competitor analysis and profile insights.",
    forUsers: ["agency", "freelancer", "local-business"],
    related: ["review-routing", "aeo-geo", "publishing", "ai-tools"],
    faqSegments: ["gmb"],
  },
  {
    id: "facebook",
    featureId: "facebook",
    name: "Facebook Automation",
    family: "ai-automation",
    status: "ACTIVE",
    definition:
      "Publishing, scheduling and a shared inbox for Facebook Pages: posts, comments and Messenger conversations for every client Page in one place.",
    forUsers: ["agency", "freelancer", "local-business"],
    related: ["instagram", "publishing", "ai-tools"],
    faqSegments: ["facebook"],
  },
  {
    id: "instagram",
    featureId: "instagram",
    name: "Instagram Automation",
    family: "ai-automation",
    status: "ACTIVE",
    definition:
      "Scheduling for Instagram posts and Reels, plus one feed for DMs, mentions and story tags, with Instagram and Facebook insights side by side.",
    forUsers: ["agency", "freelancer", "local-business"],
    related: ["facebook", "publishing", "ai-tools"],
    faqSegments: ["instagram"],
  },
  {
    id: "whatsapp",
    featureId: "whatsapp",
    name: "WhatsApp API CRM",
    family: "ai-automation",
    status: "ACTIVE",
    definition:
      "A WhatsApp CRM built on the WhatsApp API: bulk messages, broadcast campaigns, 24/7 auto replies, a chatbot that captures leads and books appointments, and campaign tracking.",
    forUsers: ["agency", "freelancer", "local-business"],
    related: ["facebook", "instagram", "appointments"],
    faqSegments: ["whatsapp"],
  },
  {
    id: "ai-calling",
    featureId: "ai-calling",
    name: "AI Calling",
    family: "ai-automation",
    status: "ACTIVE",
    definition:
      "An AI voice agent that answers inbound calls and makes outbound calls, talks to customers in natural language, qualifies leads, books appointments and keeps call logs with transcripts.",
    forUsers: ["agency", "freelancer", "local-business"],
    related: ["whatsapp", "appointments", "gmb"],
    faqSegments: [],
  },
  {
    id: "wordpress",
    featureId: "wordpress",
    name: "WordPress Publishing",
    family: "ai-automation",
    status: "ACTIVE",
    definition:
      "Publish and schedule WordPress blog posts from the same composer used for Google and social, and manage posts already on the site.",
    forUsers: ["agency", "freelancer"],
    related: ["publishing", "ai-tools", "aeo-geo"],
    faqSegments: ["wordpress"],
  },
  {
    id: "aeo-geo",
    featureId: "aeo-geo",
    name: "AEO + GEO Visibility",
    family: "ai-automation",
    status: "ACTIVE",
    definition:
      "Answer Engine Optimization (AEO) and Generative Engine Optimization (GEO) checks: scans a website for AI-readiness and AI-crawler access, shows whether AI assistants such as ChatGPT, Gemini and Perplexity surface the business, and turns the gaps into a fix-it task list.",
    forUsers: ["agency", "freelancer"],
    related: ["gmb", "wordpress"],
    faqSegments: ["aeo-geo"],
  },
  {
    id: "review-routing",
    featureId: "review-routing",
    name: "Review Booster QR",
    family: "qr-software",
    status: "ACTIVE",
    definition:
      "A QR code or link that asks customers to rate their visit first: happy customers are sent to the Google review page, unhappy ones reach the business privately.",
    forUsers: ["agency", "entrepreneur", "local-business"],
    related: ["gmb", "qr-nfc", "menu"],
    faqSegments: ["reviews", "qr-nfc"],
  },
  {
    id: "menu",
    featureId: "menu",
    name: "Smart Digital Menu",
    family: "qr-software",
    status: "ACTIVE",
    definition:
      "A QR menu for restaurants and cafes with categories, items, prices and photos that update instantly without reprinting.",
    forUsers: ["agency", "entrepreneur", "local-business"],
    related: ["review-routing", "qr-nfc", "gmb"],
    faqSegments: ["menu-card"],
  },
  {
    id: "qr-nfc",
    featureId: "qr-nfc",
    name: "Smart Digital Business Card",
    family: "qr-software",
    status: "ACTIVE",
    definition:
      "A digital visiting card with its own QR code and shareable link (NFC-enabled) that shares contact details, social links and the review page in one tap.",
    forUsers: ["agency", "entrepreneur", "local-business"],
    related: ["bio-links", "review-routing", "whatsapp"],
    faqSegments: ["qr-nfc"],
  },
  {
    id: "bio-links",
    featureId: "bio-links",
    name: "Link-in-Bio + Instant Website",
    family: "qr-software",
    status: "ACTIVE",
    definition:
      "A link-in-bio page that routes followers to a website, WhatsApp, menu, socials or offers, plus a one-page website built automatically from connected channels.",
    forUsers: ["agency", "entrepreneur", "local-business"],
    related: ["qr-nfc", "instagram", "gmb"],
    faqSegments: ["website"],
  },
  {
    id: "appointments",
    featureId: "appointments",
    name: "Appointment Scheduling",
    family: "qr-software",
    status: "ACTIVE",
    definition:
      "An online booking page with automatic WhatsApp and email reminders, deposits, calendar sync and phone, video or in-person appointments.",
    forUsers: ["agency", "entrepreneur", "local-business"],
    related: ["whatsapp", "review-routing", "gmb"],
    faqSegments: [],
  },
  {
    id: "agency",
    featureId: "agency",
    name: "White-Label Agency Platform (Agency OS)",
    family: "agency-platform",
    status: "ACTIVE",
    definition:
      "The reseller layer of MagicBolt AI: full white-label branding on the agency's own domain, client management, unlimited pricing tiers with usage limits, recurring billing and revenue metrics, a Google Maps lead finder and bulk mailing.",
    forUsers: ["agency", "freelancer", "entrepreneur"],
    related: ["publishing", "ai-tools", "gmb"],
    faqSegments: ["white-label", "agency-tools"],
  },
  {
    id: "publishing",
    featureId: "publishing",
    name: "Composer & Content Calendar",
    family: "agency-platform",
    status: "ACTIVE",
    definition:
      "Write one post and publish it to Google, Facebook, Instagram and WordPress together, with every scheduled post on one calendar and bulk media scheduling.",
    forUsers: ["agency", "freelancer", "local-business"],
    related: ["ai-tools", "facebook", "instagram", "wordpress"],
    faqSegments: [],
  },
  {
    id: "ai-tools",
    featureId: "ai-tools",
    name: "AI Agent & Autopilot",
    family: "agency-platform",
    status: "ACTIVE",
    definition:
      "AI trained on each business's brand voice that writes captions and posts, runs on autopilot (drafts for approval or auto-publish), a chat assistant, and an MCP connector to manage the business from Claude, ChatGPT or Cursor in read-and-draft mode.",
    forUsers: ["agency", "freelancer", "local-business"],
    related: ["publishing", "gmb", "facebook", "instagram"],
    faqSegments: [],
  },
];

// Problem -> solution relationships. `question` is how a user would ask
// it (the H3 heading on /product-finder); `phrases` is the language users use to
// describe the problem (used for matching); `solvedBy` lists the products
// and the specific capabilities (feature point titles in ./features.js) that
// address it. `role` explains what each product contributes.
export const PROBLEMS = [
  {
    id: "unanswered-reviews",
    question: "How can I reply to every Google review quickly?",
    title: "Google reviews go unanswered",
    phrases: ["reply to reviews", "review replies", "respond to reviews", "manage reviews", "review management", "reviews", "reputation"],
    solvedBy: [
      { product: "gmb", capabilities: ["AI Review Replies"], role: "Replies to every Google review in the business's voice, automatically or as drafts for approval." },
      { product: "review-routing", capabilities: ["Smart Sentiment Routing"], role: "Brings in more positive reviews and catches complaints privately first." },
    ],
  },
  {
    id: "more-reviews",
    question: "How can I get more Google reviews and stop bad ones hurting my rating?",
    title: "Not enough Google reviews, or bad reviews pulling the rating down",
    phrases: ["get more reviews", "more google reviews", "negative reviews", "bad reviews", "increase rating", "review qr", "review booster", "feedback", "reviews", "rating"],
    solvedBy: [
      { product: "review-routing", capabilities: ["Smart Sentiment Routing", "Printable QR Standees"], role: "Customers scan a QR standee, rate first, and happy ones go straight to Google." },
    ],
  },
  {
    id: "local-ranking",
    question: "How do I get my business to show up on Google Maps?",
    title: "Business doesn't show up on Google Maps or local search",
    phrases: ["google maps", "local seo", "rank on google", "google ranking", "not showing on google", "google business profile", "gmb", "gbp", "local search", "online presence", "near me"],
    solvedBy: [
      { product: "gmb", capabilities: ["Profile Audit Report", "One-Screen Optimization", "Geo-Grid Rank Tracking", "Competitor Analysis"], role: "Audits the profile, fixes gaps, and tracks Maps rankings by neighbourhood against competitors." },
    ],
  },
  {
    id: "ai-search-visibility",
    question: "How can my business get recommended by ChatGPT, Gemini and Perplexity?",
    title: "Business isn't recommended by ChatGPT, Gemini or Perplexity",
    phrases: ["chatgpt", "gemini", "perplexity", "ai search", "aeo", "geo", "answer engine", "generative engine", "ai visibility", "llm", "ai recommend"],
    solvedBy: [
      { product: "aeo-geo", capabilities: ["AI-Readiness Scan", "Fix-It Task List", "Score History & Comparison"], role: "Scans the site for AI-readiness and crawler access, then gives a task list to fix what blocks AI assistants." },
    ],
  },
  {
    id: "social-posting",
    question: "How can I post to all my social channels without spending hours?",
    title: "Posting regularly to every social channel takes too much time",
    phrases: ["social media", "automate social", "schedule posts", "post scheduling", "content calendar", "social media scheduler", "posting", "cross post", "online presence"],
    solvedBy: [
      { product: "publishing", capabilities: ["One Composer, Every Channel", "Content Calendar", "Bulk Media Scheduler"], role: "Write once, publish to Google, Facebook, Instagram and WordPress, and see it all on one calendar." },
      { product: "ai-tools", capabilities: ["AI Autopilot", "AI Posts & Captions"], role: "Generates the posts themselves on a daily, weekly or monthly schedule." },
      { product: "instagram", capabilities: ["Reel & Post Scheduling"], role: "Schedules Instagram posts and Reels with a live preview." },
      { product: "facebook", capabilities: ["Publish & Schedule"], role: "Publishes to Facebook Pages now or on a schedule." },
    ],
  },
  {
    id: "content-writing",
    question: "How can I create posts and captions without writing them myself?",
    title: "No time to write posts and captions",
    phrases: ["write posts", "captions", "content writing", "ai content", "generate posts", "brand voice", "copywriting"],
    solvedBy: [
      { product: "ai-tools", capabilities: ["Brand Voice Agent", "AI Autopilot", "AI Posts & Captions"], role: "Learns the brand voice once, then writes on-brand posts for every channel." },
    ],
  },
  {
    id: "missed-messages",
    question: "How do I stop missing Facebook and Instagram comments and DMs?",
    title: "Customer comments and DMs get missed across Facebook and Instagram",
    phrases: ["messages", "dm", "dms", "inbox", "comments", "messenger", "reply to comments", "instagram dm", "mentions"],
    solvedBy: [
      { product: "facebook", capabilities: ["Comments Inbox", "Unified Messenger Inbox"], role: "Puts comments and Messenger conversations from every Page in one inbox." },
      { product: "instagram", capabilities: ["DM Inbox", "Mentions & Story Tags"], role: "Shows Instagram DMs, tags and story mentions in the same place." },
    ],
  },
  {
    id: "whatsapp-leads",
    question: "How can I reply to WhatsApp enquiries 24/7 and capture every lead?",
    title: "WhatsApp enquiries go unanswered and leads are lost",
    phrases: ["whatsapp", "whatsapp api", "whatsapp crm", "whatsapp chatbot", "chatbot", "auto reply", "leads", "lead management", "crm", "enquiries", "inquiries", "follow up"],
    solvedBy: [
      { product: "whatsapp", capabilities: ["Auto Replies", "Chatbot & Lead Capture", "Appointment Booking"], role: "Answers WhatsApp chats around the clock, captures each enquiry as a lead and lets customers book inside the chat." },
    ],
  },
  {
    id: "whatsapp-campaigns",
    question: "How can I send offers to many customers on WhatsApp at once?",
    title: "Sending offers and updates on WhatsApp one chat at a time",
    phrases: ["bulk whatsapp", "whatsapp broadcast", "broadcast", "bulk messages", "whatsapp marketing", "whatsapp campaign", "whatsapp"],
    solvedBy: [
      { product: "whatsapp", capabilities: ["Bulk Messages", "Broadcast Campaigns", "Track & Grow"], role: "Sends bulk messages and broadcast campaigns to contact lists and tracks how they perform." },
    ],
  },
  {
    id: "blog-publishing",
    question: "How can I keep a WordPress blog updated regularly?",
    title: "Client WordPress blogs don't get updated",
    phrases: ["wordpress", "blog", "blog posts", "website content"],
    solvedBy: [
      { product: "wordpress", capabilities: ["Publish & Schedule Posts", "Manage Existing Content"], role: "Publishes and schedules WordPress posts from the shared composer." },
    ],
  },
  {
    id: "printed-menu",
    question: "How can a restaurant update its menu without reprinting it?",
    title: "Printed menus are costly and slow to update",
    phrases: ["menu", "qr menu", "digital menu", "restaurant menu", "menu card", "update prices"],
    solvedBy: [
      { product: "menu", capabilities: ["Instant Menu Updates", "Photos & Themes"], role: "A QR menu that updates instantly - no reprinting." },
    ],
  },
  {
    id: "business-card",
    question: "What can replace paper business cards?",
    title: "Sharing contact details still depends on paper business cards",
    phrases: ["business card", "visiting card", "nfc", "digital card", "vcard", "contact card"],
    solvedBy: [
      { product: "qr-nfc", capabilities: ["Digital Business Card", "Themes & Custom Domains"], role: "A QR / NFC card that shares contact, socials and review link in one tap." },
    ],
  },
  {
    id: "no-website",
    question: "How can I get one link or a simple website for all my profiles?",
    title: "Needs one link or a simple website for all profiles",
    phrases: ["link in bio", "bio link", "linktree", "website", "landing page", "one link", "no website", "instant website"],
    solvedBy: [
      { product: "bio-links", capabilities: ["Link-in-Bio Page", "Branded Themes"], role: "A link-in-bio page plus a one-page site built from connected channels." },
    ],
  },
  {
    id: "qr-business",
    question: "How do I start a QR-based digital software business?",
    title: "Wants to start a QR-based digital software business",
    phrases: ["qr", "qr code", "qr based", "qr business", "qr software", "digital software business"],
    solvedBy: [
      { product: "review-routing", capabilities: ["Smart Sentiment Routing", "Printable QR Standees"], role: "Review QR standees - an easy first product to sell to shops." },
      { product: "menu", capabilities: ["Instant Menu Updates"], role: "QR menus for restaurants and cafes." },
      { product: "qr-nfc", capabilities: ["Digital Business Card"], role: "QR / NFC digital business cards." },
      { product: "agency", capabilities: ["Full White-Label"], role: "Sell all of it under your own brand and domain." },
    ],
  },
  {
    id: "bookings",
    question: "How can customers book appointments online and actually show up?",
    title: "Bookings are managed by phone and customers don't show up",
    phrases: ["appointment", "appointments", "booking", "bookings", "schedule appointments", "no shows", "no-shows", "reminders", "calendar"],
    solvedBy: [
      { product: "appointments", capabilities: ["Online Booking", "Auto Reminders", "Payments & Deposits", "Calendar Sync"], role: "24/7 online booking with WhatsApp and email reminders and deposits." },
    ],
  },
  {
    id: "many-clients",
    question: "How can an agency manage many clients from one dashboard?",
    title: "Agency juggles many client accounts across separate tools",
    phrases: ["agency", "multiple clients", "manage clients", "many clients", "client accounts", "multiple locations", "multiple profiles", "manage profiles", "software for clients"],
    solvedBy: [
      { product: "agency", capabilities: ["Client Management", "Plans & Usage Limits"], role: "Every client in one agency dashboard, with one-click login to each client panel." },
      { product: "publishing", capabilities: ["One Composer, Every Channel", "Content Calendar"], role: "One calendar across all clients and channels." },
    ],
  },
  {
    id: "resell-own-brand",
    question: "How can I sell software under my own brand?",
    title: "Wants to sell software under their own brand",
    phrases: ["white label", "white-label", "resell", "reseller", "my own brand", "own saas", "start saas", "rebrand", "private label", "sell software"],
    solvedBy: [
      { product: "agency", capabilities: ["Full White-Label", "Plans & Usage Limits", "Billing & Revenue", "Bring Your Own Keys"], role: "Your logo, domain and prices on the whole platform; bill clients directly." },
    ],
  },
  {
    id: "find-clients",
    question: "How can an agency find new clients?",
    title: "Agency needs a steady flow of new clients",
    phrases: ["find clients", "get clients", "prospects", "lead generation", "new clients", "outreach", "bulk email", "cold email"],
    solvedBy: [
      { product: "agency", capabilities: ["Lead Center", "Bulk Mailing"], role: "Finds businesses on Google Maps with weak profiles and runs email outreach to them." },
      { product: "gmb", capabilities: ["Profile Audit Report"], role: "A branded audit PDF to open the sales conversation." },
    ],
  },
  {
    id: "ai-assistant",
    question: "Can I manage my business from ChatGPT or Claude?",
    title: "Wants to manage the business from ChatGPT or Claude",
    phrases: ["mcp", "claude", "chatgpt connector", "cursor", "ai assistant", "copilot"],
    solvedBy: [
      { product: "ai-tools", capabilities: ["MCP Connector", "AI Assistant"], role: "Read-and-draft access from Claude, ChatGPT or Cursor, with a person approving anything before it goes live." },
    ],
  },
];

export const getProductKB = (id) => PRODUCTS_KB.find((p) => p.id === id);
export const getProductKBByFeatureId = (featureId) => PRODUCTS_KB.find((p) => p.featureId === featureId);
export const problemsForProduct = (id) => PROBLEMS.filter((pr) => pr.solvedBy.some((s) => s.product === id));
export const industriesForProduct = (id) => INDUSTRIES.filter((ind) => ind.products.includes(id));
